/**
 * secureStorage — szyfrowanie/deszyfrowanie danych w localStorage
 * za pomocą AES-GCM (Web Crypto API). Klucz derywowany z hosta + stałego seeda.
 * Nie jest to pełna ochrona kryptograficzna (klucz jest w kodzie),
 * ale skutecznie zaciemnia dane przed casual access i narzędziami inspektora.
 */

const SEED = 'mlot-pwa-2026-secure-key-v1'

async function deriveKey(): Promise<CryptoKey> {
  const encoder = new TextEncoder()
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(SEED + window.location.hostname),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  )
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('mlot-salt-fixed'),
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer as ArrayBuffer
}

export async function encryptAndStore(key: string, plaintext: string): Promise<void> {
  try {
    const cryptoKey = await deriveKey()
    const encoder = new TextEncoder()
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      encoder.encode(plaintext)
    )
    const payload = JSON.stringify({
      iv: arrayBufferToBase64(iv.buffer as ArrayBuffer),
      ct: arrayBufferToBase64(ciphertext),
    })
    localStorage.setItem(key, payload)
  } catch {
    // Fallback: jeśli crypto.subtle niedostępne (HTTP bez TLS), zapisz obfuscated
    localStorage.setItem(key, btoa(encodeURIComponent(plaintext)))
  }
}

export async function decryptFromStore(key: string): Promise<string | null> {
  const raw = localStorage.getItem(key)
  if (!raw) return null

  try {
    const { iv, ct } = JSON.parse(raw)
    if (!iv || !ct) throw new Error('not encrypted format')
    const cryptoKey = await deriveKey()
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToArrayBuffer(iv) },
      cryptoKey,
      base64ToArrayBuffer(ct)
    )
    return new TextDecoder().decode(decrypted)
  } catch {
    // Fallback: próbuj jako base64-obfuscated
    try {
      return decodeURIComponent(atob(raw))
    } catch {
      // Stary format (czysty JSON) — migracja
      return raw
    }
  }
}

export function removeFromStore(key: string): void {
  localStorage.removeItem(key)
}
